<script lang="ts">
  /**
   * The Front Door, three pages behind one sign-in.
   *
   * It now uses `Shell` from steyn-design, which the previous single-page version
   * deliberately did not: Shell exists to frame a module with several routes, and a
   * page with a scroll-to rail was not that. It is now — Home for arriving, Status
   * for how things are running, Estate for the map — so Shell stops being ceremony
   * and starts being the thing that makes the Front Door look like the rest of the
   * estate rather than a cousin of it.
   *
   * `frontDoorUrl` is null on purpose. A link back to yourself is furniture.
   *
   * ## Two routes deliberately escape all of that
   *
   * `#/tablet` (F1) renders before the sign-in gate and without `Shell`, and
   * `#/handbook` (F26) renders behind the gate and without `Shell`. Neither is a
   * portal page: one is an object on a kitchen wall and one is a sheet of paper.
   * The order of the branches below is the feature — the tablet is checked first,
   * so the gate is not merely skipped on that route, it is unreachable.
   */
  import { Icon, Shell, SignIn, type PortalLink } from "@steyncd/steyn-design";
  import Home from "./routes/Home.svelte";
  import Status from "./routes/Status.svelte";
  import EstatePage from "./routes/Estate.svelte";
  import Handbook from "./routes/Handbook.svelte";
  import Search from "./routes/Search.svelte";
  import Tablet from "./routes/Tablet.svelte";
  import { session } from "./lib/stores.svelte";
  import { glance } from "./lib/glance.svelte";
  import { estate, estateBlurbs, ICON } from "./lib/estate.svelte";
  import { router } from "./lib/router.svelte";
  import { signIn, signOut, completeRedirect, explainAuthError } from "./lib/auth";
  import { passkey } from "./lib/passkey.svelte";

  let signingIn = $state(false);
  let err = $state<string | null>(null);

  /**
   * Finish a redirect sign-in before deciding what to render.
   *
   * When the popup path is unavailable — a blocked popup, or a storage-partitioned
   * browser — `signIn` navigates away to Google and comes back here. Without this
   * call the credential in the URL is never collected and the gate reappears, which
   * is exactly the "I click it and nothing happens" loop.
   */
  let settling = $state(true);
  $effect(() => {
    // The kitchen tablet does no auth work of any kind, and that is stronger than
    // "it does not need to": probing for a credential and collecting a redirect are
    // both steps towards asking who you are, on the one screen whose brief is that
    // it must never ask. It also has to paint on a tablet that has no session and
    // never will, so waiting on either of these would blank the wall for nothing.
    if (router.route === "tablet") {
      settling = false;
      return;
    }

    // Started alongside the redirect check rather than after it: both are boot
    // work, neither depends on the other, and the passkey probe decides which
    // button the gate paints first. Awaiting them in series would delay the one
    // screen whose entire brief was "be faster to get through".
    void passkey.probe();

    void completeRedirect()
      .then((r) => {
        // A redirect that came back with a real error used to be swallowed, so the gate
        // just reappeared with nothing saying why. Surface it like any other failure.
        if (r.error) err = explainAuthError(r.error);
      })
      .finally(() => (settling = false));
  });

  async function doSignIn() {
    signingIn = true;
    err = null;
    try {
      await signIn();
    } catch (e) {
      // Null for a cancelled popup: closing a window is a decision, not a fault.
      err = explainAuthError(e);
    } finally {
      signingIn = false;
    }
  }

  /**
   * The fast path. Failure and dismissal both fall back to Google **silently**.
   *
   * `passkey.signIn()` throws nothing and classifies nothing for us — it leaves a
   * plain sentence in `passkey.note` and returns false. That sentence goes to
   * `SignIn`'s `error` slot with no `errorCode`, because there is nothing to
   * diagnose: the Google button directly beneath it is the remedy, and dressing a
   * dismissed Face ID sheet as a fault would make the screen feel broken at the
   * exact moment it is working as designed.
   */
  async function doPasskey() {
    err = null;
    const ok = await passkey.signIn();
    if (!ok) err = passkey.note;
  }

  /**
   * The passkey prop, or null.
   *
   * Null is the Google-first rendering, and it is the default: `VITE_PASSKEY_ENABLED`
   * is off until the owner confirms the Safari sign-in fix, per `06-build-order.md`
   * — *"do not build the passkey path on top of a broken Google path"*. With the
   * flag off `passkey.ready` is false, this stays null, and the gate is exactly what
   * it was.
   */
  const passkeyProp = $derived(
    passkey.ready && passkey.name ? { name: passkey.name, onpasskey: doPasskey } : null,
  );

  /**
   * The enrolment offer, and only ever **after** a successful sign-in.
   *
   * Guarded on `claims` rather than `user` for the same reason the estate load is:
   * an account with no household access is on its way back out, and offering to
   * save it a passkey would be offering a shortcut to a door it cannot open.
   */
  $effect(() => {
    if (session.claims && session.user) void passkey.reconcile(session.firstName);
  });

  /**
   * One load for all three pages, started once the caller has claims.
   *
   * Guarded on `claims` rather than on `user`: `/portals` needs the household claim,
   * and firing it for a signed-in account with no access would 403 and paint an
   * error over the "no access" card, which reads as a fault rather than an answer.
   */
  $effect(() => {
    // Not on the tablet. If a paired display account happens to be signed in there,
    // the registry is still a request nothing on that screen renders — and a wall
    // screen polling for a portal grid it never shows is cost with no reader.
    if (router.route === "tablet") return;
    if (session.claims && !estate.loaded && !estate.loading) void estate.load();
  });

  const nav = $derived([
    { id: "home", label: "Home", icon: "door", badge: estate.attention.length || null },
    { id: "status", label: "Status", icon: "bolt" },
    { id: "estate", label: "The estate", icon: "finance" },
  ]);

  /**
   * The cross-portal switcher in the rail. Fed from the registry we already hold, so
   * it costs no second request — Shell takes a loader and caches the result.
   */
  async function portals(): Promise<PortalLink[]> {
    if (!estate.loaded) await estate.load();
    return estate.visible
      .filter((p) => p.status === "live")
      .map((p) => ({ id: p.id, name: p.name, url: p.url, icon: ICON[p.id] ?? "door" }));
  }
</script>

{#if router.route === "tablet"}
  <!-- F1, and the position of this branch is the feature.
       Checked before `session.ready`, before the redirect settles and before the
       gate, so the kitchen screen paints its clock, its date and its verse on the
       first frame and never shows a sign-in card at all. Everything it can read
       without a token it reads; everything it cannot, it says so about. -->
  <Tablet />
{:else if !session.ready || settling}
  <!-- Deliberately blank. Flashing the sign-in card and then replacing it with the
       app is worse than 200ms of nothing — and while a redirect is settling, the
       gate would be actively wrong. -->
  <div class="boot" aria-busy="true"></div>
{:else if !session.user || !session.claims}
  <SignIn
    glance={glance.stats}
    product="Steyn Front Door"
    mark="door"
    tagline="The Steyn household's front door. Every portal, one sign-in."
    verse="But as for me and my house, we will serve the LORD."
    verseRef="Joshua 24:15"
    onsignin={doSignIn}
    onsignout={signOut}
    passkey={passkeyProp}
    busy={signingIn || passkey.busy}
    error={err}
    noAccess={Boolean(session.user && !session.claims)}
    location="Pretoria East"
    portals={estateBlurbs()}
  />
{:else if router.route === "handbook"}
  <!-- F26. Behind the gate — it lists every address in the estate — but outside
       `Shell`, because it is a document rather than a page and the rail and header
       are precisely what must not reach the paper. See the note at the top of
       Handbook.svelte for why that is a layout decision rather than a print one. -->
  <Handbook />
{:else}
  <Shell
    product="Front Door"
    mark="door"
    surface="home"
    {nav}
    active={router.route}
    onnavigate={(id) => router.go(id as "home" | "status" | "estate")}
    homeHref="#/"
    frontDoorUrl={null}
    {portals}
  >
    {#snippet header()}
      <Search />
      <span class="who">
        <Icon name="user" size={14} />
        <span class="who__name">{session.user?.displayName ?? session.user?.email}</span>
      </span>
      <button class="iconbtn" onclick={signOut} aria-label="Sign out"><Icon name="logout" size={16} /></button>
    {/snippet}

    {#if passkey.offer}
      <!-- After sign-in, never before. `02-login.md`: a prompt to create a
           credential in front of somebody who has not yet got in is the fastest
           way to make a login screen slower. It is a strip on the page they
           asked for, not a modal over it, and "Not now" is a real button of the
           same size rather than a grey word in a corner. -->
      <aside class="offer">
        <span class="offer__ico" aria-hidden="true"><Icon name="passkey" size={18} /></span>
        <div class="offer__text">
          <p class="offer__lead">Next time, one tap.</p>
          <p class="offer__sub">{passkey.enrolNote()}</p>
        </div>
        <div class="offer__acts">
          <button class="offer__btn offer__btn--go" onclick={() => passkey.enrol(session.firstName)} disabled={passkey.busy}>
            {passkey.busy ? "Waiting…" : "Save a passkey"}
          </button>
          <button class="offer__btn" onclick={() => passkey.skip()}>Not now</button>
        </div>
      </aside>
    {/if}

    {#if router.route === "status"}
      <Status />
    {:else if router.route === "estate"}
      <EstatePage />
    {:else}
      <Home />
    {/if}
  </Shell>
{/if}

<style>
  /* Shown while Firebase restores a session and while a redirect settles. It has to
     paint the estate background, or the moment before the app appears is a white
     flash — which on a phone reads as the page having failed. */
  .boot {
    min-height: 100vh;
    background: var(--aurora);
  }

  /* The passkey offer. Copper, which is the estate's "this is ours" accent, and
     never the only signal — a glyph and a sentence carry it too. No green
     anywhere; this is an invitation rather than a success. */
  .offer {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 12px 14px;
    margin-bottom: 16px;
    border: 1px solid var(--line);
    border-left: 3px solid var(--acc);
    border-radius: var(--r-card, 14px);
    background: var(--fill);
  }
  .offer__ico {
    display: inline-flex;
    color: var(--acc);
  }
  .offer__text {
    flex: 1 1 260px;
    min-width: 0;
  }
  .offer__lead {
    margin: 0;
    font-size: 0.875rem;
    font-weight: 700;
    color: var(--tx);
  }
  .offer__sub {
    /* 0.75rem = 12px. The estate's floor is 11.5px and this is a sentence
       somebody has to read once and act on, so it sits above the floor. */
    margin: 2px 0 0;
    font-size: 0.75rem;
    line-height: 1.45;
    color: var(--mut);
  }
  .offer__acts {
    display: flex;
    gap: 8px;
  }
  .offer__btn {
    /* 44px clears the 24×24 minimum with room; a decline button that is harder
       to hit than the accept button is a dark pattern. Both are the same size. */
    min-height: 44px;
    padding: 0 14px;
    border: 1px solid var(--line);
    border-radius: var(--r-control, 10px);
    background: transparent;
    color: var(--tx);
    font: inherit;
    font-size: 0.8125rem;
    font-weight: 700;
    cursor: pointer;
  }
  .offer__btn--go {
    border-color: var(--acc);
    color: var(--acc);
  }
  .offer__btn:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .who {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
    min-width: 0;
  }
  .who__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  @media (max-width: 620px) {
    /* The search field needs the room more than the name does. */
    .who {
      display: none;
    }
  }
</style>
